import {
  DEFAULT_MOCK_SCENARIO_ID,
  getScenario,
  isMockScenarioId,
  MockScenario,
  MockScenarioId,
} from "./scenarios";

export const MOCK_SCENARIO_STORAGE_KEY = "primio-monitor.mockScenario";
export const MOCK_SCENARIO_CHANGE_EVENT = "primio-monitor:mock-scenario";

/** True in CRA development, or when Cypress/preview explicitly enables mocks. */
export function isMockAllowed(): boolean {
  return (
    process.env.NODE_ENV === "development" ||
    process.env.REACT_APP_ENABLE_MOCK === "true"
  );
}

function readQueryScenario(): MockScenarioId | null {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("mock");
  if (!raw) return null;
  // `?mock=1` or `?mock=true` → use stored/default scenario
  if (raw === "1" || raw === "true") return null;
  return isMockScenarioId(raw) ? raw : null;
}

function readStoredScenario(): MockScenarioId | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(MOCK_SCENARIO_STORAGE_KEY);
    return raw && isMockScenarioId(raw) ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Resolves the active mock scenario, or null when mock mode is off.
 * Priority: ?mock=<id> → localStorage → default when REACT_APP_ENABLE_MOCK.
 */
export function resolveMockScenario(): MockScenario | null {
  if (!isMockAllowed()) return null;

  const fromQuery = readQueryScenario();
  if (fromQuery) {
    return getScenario(fromQuery) ?? null;
  }

  const hasMockQuery =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("mock");

  const fromStorage = readStoredScenario();
  if (fromStorage) {
    return getScenario(fromStorage) ?? null;
  }

  if (hasMockQuery || process.env.REACT_APP_ENABLE_MOCK === "true") {
    return getScenario(DEFAULT_MOCK_SCENARIO_ID) ?? null;
  }

  return null;
}

export function isMockModeActive(): boolean {
  return resolveMockScenario() !== null;
}

/** Persist scenario, sync URL `?mock=`, and notify listeners. */
export function setMockScenario(id: MockScenarioId): void {
  if (!isMockAllowed() || typeof window === "undefined") return;

  try {
    window.localStorage.setItem(MOCK_SCENARIO_STORAGE_KEY, id);
  } catch {
    // ignore quota / private mode
  }

  const url = new URL(window.location.href);
  url.searchParams.set("mock", id);
  window.history.replaceState({}, "", url.toString());

  window.dispatchEvent(
    new CustomEvent(MOCK_SCENARIO_CHANGE_EVENT, { detail: { id } }),
  );
}

export function clearMockScenario(): void {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(MOCK_SCENARIO_STORAGE_KEY);
  } catch {
    // ignore
  }

  const url = new URL(window.location.href);
  url.searchParams.delete("mock");
  window.history.replaceState({}, "", url.toString());

  window.dispatchEvent(
    new CustomEvent(MOCK_SCENARIO_CHANGE_EVENT, { detail: { id: null } }),
  );
}
